type BrowserLockManager = {
  request<T>(
    name: string,
    callback: (lock: unknown) => T | Promise<T>,
  ): Promise<T>
}

type AlertRecord = {
  eventKey: string
  tabID?: string
  nonce?: string
  expiresAt: number
}

type ObservedAlertRecord = {
  eventKey: string
  observedAt: number
  expiresAt: number
}

export const playerSoundIDs = [
  'readyFocused',
  'readyNoFocus',
  'accepted',
  'declined',
  'connect',
  'searchStart',
  'searchCancel',
  'panelOpen',
  'panelClose',
  'queueChange',
  'chatMessage',
  'socialNotification',
] as const

export type PlayerSoundID = typeof playerSoundIDs[number]

export type PlayerSoundSettingsSnapshot = {
  master: number
  volumes: Record<PlayerSoundID, number>
}

type PlayerSoundDefinition = {
  url: string
  gain: number
  maxStartDelayMS: number
}

type PlayerSoundSlot = {
  audio: HTMLAudioElement
  primed: boolean
  primePromise: Promise<void> | null
  primeToken: number
  playbackToken: number
  releasePlayback: (() => void) | null
}

export type PlayerSoundDeliveryKind =
  | 'ready_check'
  | 'match_connect'
  | 'chat'
  | 'social'

export type PlayerSoundDeliveryOutcome =
  | 'observed'
  | 'visual_shown'
  | 'audio_started'
  | 'audio_failed'
  | 'stale'
  | 'suppressed'

export type PlayerSoundDeliveryEvent = {
  kind: PlayerSoundDeliveryKind
  eventKey: string
  soundID: PlayerSoundID
  outcome: PlayerSoundDeliveryOutcome
  occurredAt: number
  tabID: string
  expiresAt?: number
  serverEventAt?: string
  errorName?: string
}

export type PlayerCriticalSoundOptions = {
  observedAt?: number
  expiresAt?: number
  serverEventAt?: string
}

export type PlayerCriticalSoundState = {
  readyCheckEventKey?: string | null
  matchConnectEventKey?: string | null
}

type PlayerSoundPlayResult = {
  outcome: 'scheduled' | 'suppressed' | 'stale' | 'failed'
  errorName?: string
}

type CriticalDispatch = {
  eventKey: string
  publicEventKey: string
  kind: Extract<PlayerSoundDeliveryKind, 'ready_check' | 'match_connect'>
  soundID: PlayerSoundID
  expiresAt: number
  serverEventAt?: string
  fire: (requestedAt: number, expiresAt: number) => Promise<PlayerSoundPlayResult>
}

const playerSoundDefinitions: Record<PlayerSoundID, PlayerSoundDefinition> = {
  readyFocused: {
    url: '/shared/assets/match_ready.mp3',
    gain: 0.9,
    maxStartDelayMS: 2_500,
  },
  readyNoFocus: {
    url: '/shared/assets/match_ready_no_focus.wav',
    gain: 0.9,
    maxStartDelayMS: 2_500,
  },
  accepted: {
    url: '/shared/assets/ui_button_click_01.wav',
    gain: 0.8,
    maxStartDelayMS: 1_200,
  },
  declined: {
    url: '/shared/assets/matchmaking_cancel.wav',
    gain: 0.85,
    maxStartDelayMS: 1_200,
  },
  connect: {
    url: '/shared/assets/ui_findmatch_join_01.wav',
    gain: 0.85,
    maxStartDelayMS: 2_500,
  },
  searchStart: {
    url: '/shared/assets/panorama_find_match_find_01.wav',
    gain: 0.85,
    maxStartDelayMS: 1_200,
  },
  searchCancel: {
    url: '/shared/assets/panorama_find_match_cancel_01.wav',
    gain: 0.85,
    maxStartDelayMS: 1_200,
  },
  panelOpen: {
    url: '/shared/assets/panorama_find_match_slide_in_01.wav',
    gain: 0.85,
    maxStartDelayMS: 1_200,
  },
  panelClose: {
    url: '/shared/assets/panorama_find_match_slide_out_01.wav',
    gain: 0.85,
    maxStartDelayMS: 1_200,
  },
  queueChange: {
    url: '/shared/assets/panorama_find_match_change_options_01.wav',
    gain: 0.75,
    maxStartDelayMS: 1_200,
  },
  chatMessage: {
    url: '/shared/assets/ui_xp_click_01.wav',
    gain: 0.8,
    maxStartDelayMS: 1_500,
  },
  socialNotification: {
    url: '/shared/assets/ui_unit_select_01.wav',
    gain: 0.8,
    maxStartDelayMS: 1_500,
  },
}

const lockNamePrefix = 'dota688.player-sound'
const seenStoragePrefix = 'dota688.ready-check-alert.seen:'
const claimStoragePrefix = 'dota688.ready-check-alert.claim:'
const observedStoragePrefix = 'dota688.player-sound-observed:'
const volumeStorageKey = 'dota688.sound.volume'
const soundVolumesStorageKey = 'dota688.sound.volumes.v1'
const observedTTLMS = 24 * 60 * 60 * 1000
const seenTTLMS = observedTTLMS
const claimTTLMS = 15 * 1000
const primeTimeoutMS = 800
const storageSweepIntervalMS = 60 * 1000
const criticalFallbackWindowMS = 30 * 1000
const criticalRetryDelaysMS = [250, 1_000, 5_000, 15_000, 30_000] as const
const tabID = randomToken()

const playerSoundSlots = new Map<PlayerSoundID, PlayerSoundSlot>()
const activeAudio = new Set<HTMLAudioElement>()
const seenAlerts = new Map<string, number>()
const observedAlerts = new Map<string, ObservedAlertRecord>()
const soundSettingsListeners = new Set<
  (settings: PlayerSoundSettingsSnapshot) => void
>()
const soundDeliveryListeners = new Set<
  (event: PlayerSoundDeliveryEvent) => void
>()
const reportedSoundDeliveries = new Set<string>()
const criticalRetryTimers = new Map<string, number>()
const criticalRetryAttempts = new Map<string, number>()
const criticalPendingDispatches = new Map<string, CriticalDispatch>()
const playbackAbortListeners = new Set<() => void>()
let soundVolume = readStoredVolume()
let playerSoundVolumes = readStoredSoundVolumes()
let lastStorageSweepAt = 0
let pageFrozen = false
let playerPushNotificationChannelActive = false

export function getSoundVolume(): number {
  return soundVolume
}

export function setSoundVolume(volume: number): void {
  soundVolume = clampVolume(volume)
  try {
    window.localStorage.setItem(volumeStorageKey, String(soundVolume))
  } catch {
    // localStorage can be unavailable in private or locked-down contexts.
  }
  notifySoundSettingsChanged()
}

export function getPlayerSoundVolume(soundID: PlayerSoundID): number {
  return playerSoundVolumes[soundID]
}

export function setPlayerSoundVolume(
  soundID: PlayerSoundID,
  volume: number,
): void {
  if (!isPlayerSoundID(soundID)) return
  playerSoundVolumes = {
    ...playerSoundVolumes,
    [soundID]: clampVolume(volume),
  }
  writeStoredSoundVolumes()
  notifySoundSettingsChanged()
}

export function getPlayerSoundSettings(): PlayerSoundSettingsSnapshot {
  return {
    master: soundVolume,
    volumes: { ...playerSoundVolumes },
  }
}

export function subscribePlayerSoundSettings(
  listener: (settings: PlayerSoundSettingsSnapshot) => void,
): () => void {
  soundSettingsListeners.add(listener)
  return () => soundSettingsListeners.delete(listener)
}

export function subscribePlayerSoundDelivery(
  listener: (event: PlayerSoundDeliveryEvent) => void,
): () => void {
  soundDeliveryListeners.add(listener)
  return () => soundDeliveryListeners.delete(listener)
}

export function preparePlayerSoundAssets(): void {
  for (const soundID of playerSoundIDs) {
    prepareSound(soundID)
  }
}

export function prepareReadyCheckAlert(): void {
  unlockSounds(['readyFocused', 'readyNoFocus', 'connect'])
}

export function setPlayerPushNotificationChannelActive(active: boolean): void {
  playerPushNotificationChannelActive = active
}

export function preparePlayerNotificationAudio(): void {
  unlockSounds(playerSoundIDs)
  retryPendingCriticalSounds()
}

export function dispatchReadyCheckAlertOnce(
  eventKey: string,
  modeLabel: string,
  options: PlayerCriticalSoundOptions = {},
): void {
  if (!eventKey) return
  const dispatchKey = `ready:${eventKey}`
  const observedAt = normalizeObservedAt(options.observedAt)
  const expiresAt = criticalExpiry(dispatchKey, observedAt, options.expiresAt)
  reportSoundDeliveryOnce({
    kind: 'ready_check',
    eventKey,
    soundID: readyCheckSoundID(),
    outcome: 'observed',
    occurredAt: observedAt,
    expiresAt,
    ...(options.serverEventAt ? { serverEventAt: options.serverEventAt } : {}),
  })
  void dispatchCritical({
    eventKey: dispatchKey,
    publicEventKey: eventKey,
    kind: 'ready_check',
    soundID: readyCheckSoundID(),
    expiresAt,
    ...(options.serverEventAt ? { serverEventAt: options.serverEventAt } : {}),
    fire: (requestedAt, hardDeadline) => (
      fireReadyCheckAlert(
        eventKey,
        modeLabel,
        requestedAt,
        hardDeadline,
        options.serverEventAt,
      )
    ),
  })
}

export function dispatchIncomingChatSoundOnce(
  eventKey: string,
  observedAt = Date.now(),
): void {
  if (!eventKey) return
  void dispatchOnce(`chat:${eventKey}`, requestedAt => {
    return playSoundHandled('chat', eventKey, 'chatMessage', requestedAt)
  }, observedAt)
}

export function dispatchIncomingSocialSoundOnce(
  eventKey: string,
  observedAt = Date.now(),
): void {
  if (!eventKey) return
  void dispatchOnce(`social:${eventKey}`, requestedAt => {
    return playSoundHandled('social', eventKey, 'socialNotification', requestedAt)
  }, observedAt)
}

export function playReadyCheckAcceptSound(): void {
  void playSound('accepted')
}

export function playReadyCheckDeclineSound(): void {
  void playSound('declined')
}

export function playMatchSearchStartSound(): void {
  void playSound('searchStart')
}

export function playMatchSearchCancelSound(): void {
  void playSound('searchCancel')
}

export function playMatchSearchPanelOpenSound(): void {
  void playSound('panelOpen')
}

export function playMatchSearchPanelCloseSound(): void {
  void playSound('panelClose')
}

export function playMatchSearchQueueChangeSound(): void {
  void playSound('queueChange')
}

export function dispatchMatchConnectSoundOnce(
  eventKey: string,
  options: PlayerCriticalSoundOptions = {},
): void {
  if (!eventKey) return
  const dispatchKey = `connect:${eventKey}`
  const observedAt = normalizeObservedAt(options.observedAt)
  const expiresAt = criticalExpiry(dispatchKey, observedAt, options.expiresAt)
  reportSoundDeliveryOnce({
    kind: 'match_connect',
    eventKey,
    soundID: 'connect',
    outcome: 'observed',
    occurredAt: observedAt,
    expiresAt,
    ...(options.serverEventAt ? { serverEventAt: options.serverEventAt } : {}),
  })
  void dispatchCritical({
    eventKey: dispatchKey,
    publicEventKey: eventKey,
    kind: 'match_connect',
    soundID: 'connect',
    expiresAt,
    ...(options.serverEventAt ? { serverEventAt: options.serverEventAt } : {}),
    fire: (requestedAt, hardDeadline) => (
      playCriticalSound(
        'match_connect',
        eventKey,
        'connect',
        requestedAt,
        hardDeadline,
        options.serverEventAt,
      )
    ),
  })
}

// Reconciles retryable critical cues against one authoritative matchmaker
// snapshot. A freeze can suspend timers while the ready-check ends elsewhere;
// an unrelated later user gesture must not revive that obsolete sound.
export function reconcileCriticalPlayerSounds(
  state: PlayerCriticalSoundState,
): void {
  const active = new Set<string>()
  if (state.readyCheckEventKey) {
    active.add(`ready:${state.readyCheckEventKey}`)
  }
  if (state.matchConnectEventKey) {
    active.add(`connect:${state.matchConnectEventKey}`)
  }
  for (const dispatchKey of [...criticalPendingDispatches.keys()]) {
    if (active.has(dispatchKey)) continue
    markSeenAlert(dispatchKey)
    clearCriticalDispatch(dispatchKey)
  }
}

export function cancelCriticalPlayerSound(
  kind: 'ready_check' | 'match_connect',
  eventKey: string,
): void {
  if (!eventKey) return
  const dispatchKey = `${kind === 'ready_check' ? 'ready' : 'connect'}:${eventKey}`
  markSeenAlert(dispatchKey)
  clearCriticalDispatch(dispatchKey)
}

export function previewPlayerSound(soundID: PlayerSoundID): void {
  if (!isPlayerSoundID(soundID)) return
  void playSound(soundID)
}

async function dispatchOnce(
  eventKey: string,
  fire: (requestedAt: number) => boolean | Promise<boolean>,
  observedAt = Date.now(),
): Promise<void> {
  if (hasSeenAlert(eventKey)) return
  const requestedAt = firstObservedAt(eventKey, observedAt)

  const locks = browserLocks()
  if (locks) {
    try {
      await locks.request(alertLockName(eventKey), async () => {
        if (hasSeenAlert(eventKey)) return
        if (await fire(requestedAt)) markSeenAlert(eventKey)
      })
      return
    } catch {
      // Fall back to localStorage coordination below.
    }
  }

  for (let attempt = 0; attempt < 3; attempt += 1) {
    if (hasSeenAlert(eventKey)) return
    const nonce = await claimAlert(eventKey)
    if (!nonce) {
      await sleep(80)
      continue
    }
    try {
      if (await fire(requestedAt)) markSeenAlert(eventKey)
    } finally {
      releaseAlertClaim(eventKey, nonce)
    }
    return
  }
}

async function dispatchCritical(dispatch: CriticalDispatch): Promise<void> {
  if (hasSeenAlert(dispatch.eventKey)) {
    clearCriticalDispatch(dispatch.eventKey)
    return
  }
  criticalPendingDispatches.set(dispatch.eventKey, dispatch)
  if (Date.now() >= dispatch.expiresAt) {
    reportCriticalExpiry(dispatch)
    markSeenAlert(dispatch.eventKey)
    clearCriticalDispatch(dispatch.eventKey)
    return
  }

  const attempt = async (): Promise<boolean> => {
    if (hasSeenAlert(dispatch.eventKey)) return true
    if (Date.now() >= dispatch.expiresAt) {
      reportCriticalExpiry(dispatch)
      return true
    }
    const result = await dispatch.fire(Date.now(), dispatch.expiresAt)
    return criticalResultIsTerminal(result, dispatch.expiresAt)
  }

  const locks = browserLocks()
  if (locks) {
    try {
      await locks.request(alertLockName(dispatch.eventKey), async () => {
        if (await attempt()) markSeenAlert(dispatch.eventKey)
      })
      if (hasSeenAlert(dispatch.eventKey)) {
        clearCriticalDispatch(dispatch.eventKey)
      } else {
        scheduleCriticalRetry(dispatch)
      }
      return
    } catch {
      // Fall back to the localStorage lease below.
    }
  }

  const nonce = await claimAlert(dispatch.eventKey)
  if (!nonce) {
    if (!hasSeenAlert(dispatch.eventKey)) scheduleCriticalRetry(dispatch)
    return
  }
  try {
    if (await attempt()) markSeenAlert(dispatch.eventKey)
  } finally {
    releaseAlertClaim(dispatch.eventKey, nonce)
  }
  if (hasSeenAlert(dispatch.eventKey)) {
    clearCriticalDispatch(dispatch.eventKey)
  } else {
    scheduleCriticalRetry(dispatch)
  }
}

function criticalResultIsTerminal(
  result: PlayerSoundPlayResult,
  expiresAt: number,
): boolean {
  if (Date.now() >= expiresAt) return true
  return result.outcome === 'scheduled' ||
    result.outcome === 'suppressed'
}

function scheduleCriticalRetry(dispatch: CriticalDispatch): void {
  if (
    hasSeenAlert(dispatch.eventKey) ||
    Date.now() >= dispatch.expiresAt ||
    pageFrozen ||
    criticalRetryTimers.has(dispatch.eventKey)
  ) {
    return
  }
  const attempt = criticalRetryAttempts.get(dispatch.eventKey) ?? 0
  const delay = criticalRetryDelaysMS[
    Math.min(attempt, criticalRetryDelaysMS.length - 1)
  ]
  criticalRetryAttempts.set(dispatch.eventKey, attempt + 1)
  const remaining = dispatch.expiresAt - Date.now()
  const timer = window.setTimeout(() => {
    criticalRetryTimers.delete(dispatch.eventKey)
    void dispatchCritical(dispatch)
  }, Math.max(0, Math.min(delay, remaining)))
  criticalRetryTimers.set(dispatch.eventKey, timer)
}

function retryPendingCriticalSounds(): void {
  if (pageFrozen) return
  for (const dispatch of criticalPendingDispatches.values()) {
    const timer = criticalRetryTimers.get(dispatch.eventKey)
    if (timer !== undefined) {
      window.clearTimeout(timer)
      criticalRetryTimers.delete(dispatch.eventKey)
    }
    void dispatchCritical(dispatch)
  }
}

function clearCriticalDispatch(eventKey: string): void {
  const timer = criticalRetryTimers.get(eventKey)
  if (timer !== undefined) window.clearTimeout(timer)
  criticalRetryTimers.delete(eventKey)
  criticalRetryAttempts.delete(eventKey)
  criticalPendingDispatches.delete(eventKey)
}

async function fireReadyCheckAlert(
  eventKey: string,
  _modeLabel: string,
  requestedAt: number,
  expiresAt: number,
  serverEventAt?: string,
): Promise<PlayerSoundPlayResult> {
  const soundID = readyCheckSoundID()
  showReadyCheckNotificationOnce(
    eventKey,
    soundID,
    expiresAt,
    serverEventAt,
  )
  return playCriticalSound(
    'ready_check',
    eventKey,
    soundID,
    requestedAt,
    expiresAt,
    serverEventAt,
  )
}

function showReadyCheckNotificationOnce(
  eventKey: string,
  soundID: PlayerSoundID,
  expiresAt: number,
  serverEventAt?: string,
): boolean {
  const visualKey = `visual:ready:${eventKey}`
  if (hasSeenAlert(visualKey)) return false
  if (playerPushNotificationChannelActive) return false
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return false
  try {
    const notification = new Notification('Ваша игра готова', {
      body: 'Откройте сайт и примите ready-check',
      tag: eventKey,
      silent: true,
    })
    notification.onclick = () => {
      window.focus()
      notification.close()
    }
    window.setTimeout(() => notification.close(), 30 * 1000)
    markSeenAlert(visualKey)
    reportSoundDeliveryOnce({
      kind: 'ready_check',
      eventKey,
      soundID,
      outcome: 'visual_shown',
      occurredAt: Date.now(),
      expiresAt,
      ...(serverEventAt ? { serverEventAt } : {}),
    })
    return true
  } catch {
    // Browser notifications can be unavailable on non-secure origins.
    return false
  }
}

function playerSoundSlot(soundID: PlayerSoundID): PlayerSoundSlot | null {
  if (typeof Audio === 'undefined') return null
  let slot = playerSoundSlots.get(soundID)
  if (!slot) {
    const audio = new Audio(playerSoundDefinitions[soundID].url)
    audio.preload = 'auto'
    try {
      audio.load()
    } catch {
      // Setting preload is still useful when load() is unavailable.
    }
    slot = {
      audio,
      primed: false,
      primePromise: null,
      primeToken: 0,
      playbackToken: 0,
      releasePlayback: null,
    }
    playerSoundSlots.set(soundID, slot)
  }
  return slot
}

function prepareSound(soundID: PlayerSoundID): void {
  playerSoundSlot(soundID)
}

function unlockSounds(soundIDs: readonly PlayerSoundID[]): void {
  for (const soundID of soundIDs) {
    primeSound(soundID)
  }
}

function primeSound(soundID: PlayerSoundID): void {
  const slot = playerSoundSlot(soundID)
  if (!slot || slot.primed || slot.primePromise || activeAudio.has(slot.audio)) return
  const { audio } = slot
  const muted = audio.muted
  const volume = audio.volume
  slot.primeToken += 1
  const primeToken = slot.primeToken
  audio.muted = true
  let played: Promise<void> | undefined
  try {
    played = audio.play()
  } catch {
    audio.muted = muted
    audio.volume = volume
    return
  }
  if (!played) {
    audio.pause()
    resetAudio(audio)
    audio.muted = muted
    audio.volume = volume
    slot.primed = true
    return
  }
  const primePromise = primeResultBeforeTimeout(played)
    .then(succeeded => {
      if (slot.primeToken !== primeToken) return
      audio.pause()
      resetAudio(audio)
      slot.primed = succeeded
    })
    .finally(() => {
      if (slot.primeToken !== primeToken) return
      audio.muted = muted
      audio.volume = volume
      if (slot.primePromise === primePromise) slot.primePromise = null
    })
  slot.primePromise = primePromise
}

async function playSound(
  soundID: PlayerSoundID,
  requestedAt = Date.now(),
  hardDeadline?: number,
): Promise<PlayerSoundPlayResult> {
  const definition = playerSoundDefinitions[soundID]
  const effectiveVolume = clampVolume(
    definition.gain * soundVolume * playerSoundVolumes[soundID],
  )
  if (effectiveVolume <= 0) return { outcome: 'suppressed' }
  if (soundRequestExpired(soundID, requestedAt, hardDeadline)) {
    return { outcome: 'stale' }
  }

  const slot = playerSoundSlot(soundID)
  if (!slot) return { outcome: 'failed', errorName: 'audio_unavailable' }
  const deadline = Math.min(
    requestedAt + definition.maxStartDelayMS,
    hardDeadline ?? Number.POSITIVE_INFINITY,
  )
  takeOverPendingPrime(slot)
  if (Date.now() >= deadline) return { outcome: 'stale' }

  const { audio } = slot
  slot.releasePlayback?.()
  slot.playbackToken += 1
  const playbackToken = slot.playbackToken
  try {
    audio.pause()
    resetAudio(audio)
  } catch {
    // play() below is the authoritative capability check.
  }
  try {
    audio.muted = false
    audio.volume = effectiveVolume
    activeAudio.add(audio)
    const release = () => {
      audio.removeEventListener('ended', release)
      audio.removeEventListener('error', release)
      if (slot.releasePlayback === release) slot.releasePlayback = null
      if (slot.playbackToken === playbackToken) activeAudio.delete(audio)
    }
    slot.releasePlayback = release
    audio.addEventListener('ended', release, { once: true })
    audio.addEventListener('error', release, { once: true })
    const played = audio.play()
    if (!played) {
      slot.primed = true
      return { outcome: 'scheduled' }
    }
    const result = await promiseResultBefore(played, deadline)
    if (result.outcome === 'scheduled') {
      if (slot.playbackToken === playbackToken) slot.primed = true
      return result
    }
    release()
    if (slot.playbackToken === playbackToken) {
      slot.primed = false
      audio.pause()
      resetAudio(audio)
    }
    return result
  } catch (error) {
    slot.releasePlayback?.()
    if (slot.playbackToken === playbackToken) slot.primed = false
    // Ignore playback failures; the overlay is still visible.
    return { outcome: 'failed', errorName: errorName(error) }
  }
}

async function playSoundHandled(
  kind: Extract<PlayerSoundDeliveryKind, 'chat' | 'social'>,
  eventKey: string,
  soundID: PlayerSoundID,
  requestedAt: number,
): Promise<boolean> {
  const result = await playSound(soundID, requestedAt)
  reportPlayResult(kind, eventKey, soundID, result)
  return result.outcome !== 'failed'
}

async function playCriticalSound(
  kind: Extract<PlayerSoundDeliveryKind, 'ready_check' | 'match_connect'>,
  eventKey: string,
  soundID: PlayerSoundID,
  requestedAt: number,
  expiresAt: number,
  serverEventAt?: string,
): Promise<PlayerSoundPlayResult> {
  const result = await playSound(soundID, requestedAt, expiresAt)
  reportPlayResult(kind, eventKey, soundID, result, {
    expiresAt,
    ...(serverEventAt ? { serverEventAt } : {}),
  })
  return result
}

function reportPlayResult(
  kind: PlayerSoundDeliveryKind,
  eventKey: string,
  soundID: PlayerSoundID,
  result: PlayerSoundPlayResult,
  context: Pick<PlayerSoundDeliveryEvent, 'expiresAt' | 'serverEventAt'> = {},
): void {
  const outcome: PlayerSoundDeliveryOutcome = result.outcome === 'scheduled'
    ? 'audio_started'
    : result.outcome === 'failed'
      ? 'audio_failed'
      : result.outcome
  reportSoundDeliveryOnce({
    kind,
    eventKey,
    soundID,
    outcome,
    occurredAt: Date.now(),
    ...context,
    ...(result.errorName ? { errorName: result.errorName } : {}),
  })
}

function reportSoundDeliveryOnce(
  event: Omit<PlayerSoundDeliveryEvent, 'tabID'>,
): void {
  const reportKey = [
    event.kind,
    event.eventKey,
    event.outcome,
    event.errorName ?? '',
  ].join(':')
  if (reportedSoundDeliveries.has(reportKey)) return
  if (reportedSoundDeliveries.size >= 4_096) {
    const oldest = reportedSoundDeliveries.values().next().value
    if (typeof oldest === 'string') reportedSoundDeliveries.delete(oldest)
  }
  reportedSoundDeliveries.add(reportKey)
  const delivery = { ...event, tabID }
  for (const listener of soundDeliveryListeners) {
    try {
      listener(delivery)
    } catch {
      // Diagnostics must never interfere with notification delivery.
    }
  }
}

function reportCriticalExpiry(dispatch: CriticalDispatch): void {
  reportSoundDeliveryOnce({
    kind: dispatch.kind,
    eventKey: dispatch.publicEventKey,
    soundID: dispatch.soundID,
    outcome: 'stale',
    occurredAt: Date.now(),
    expiresAt: dispatch.expiresAt,
    ...(dispatch.serverEventAt
      ? { serverEventAt: dispatch.serverEventAt }
      : {}),
  })
}

function criticalExpiry(
  dispatchKey: string,
  observedAt: number,
  candidate?: number,
): number {
  const firstObserved = firstObservedAt(dispatchKey, observedAt)
  if (typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0) {
    return candidate
  }
  return firstObserved + criticalFallbackWindowMS
}

function normalizeObservedAt(candidate?: number): number {
  const now = Date.now()
  return validObservedAt(candidate ?? now, now)
}

function readyCheckSoundID(): Extract<PlayerSoundID, 'readyFocused' | 'readyNoFocus'> {
  return isFocusedPage() ? 'readyFocused' : 'readyNoFocus'
}

function errorName(error: unknown): string {
  if (error instanceof Error && error.name) return error.name.slice(0, 64)
  if (
    typeof error === 'object' &&
    error !== null &&
    'name' in error &&
    typeof error.name === 'string'
  ) {
    return error.name.slice(0, 64)
  }
  return 'playback_error'
}

function soundRequestExpired(
  soundID: PlayerSoundID,
  requestedAt: number,
  hardDeadline?: number,
): boolean {
  return Date.now() >= Math.min(
    requestedAt + playerSoundDefinitions[soundID].maxStartDelayMS,
    hardDeadline ?? Number.POSITIVE_INFINITY,
  )
}

async function primeResultBeforeTimeout(
  promise: Promise<void>,
): Promise<boolean> {
  let timer: number | undefined
  try {
    return await Promise.race([
      Promise.resolve(promise).then(() => true, () => false),
      new Promise<boolean>(resolve => {
        timer = window.setTimeout(() => resolve(false), primeTimeoutMS)
      }),
    ])
  } finally {
    if (timer !== undefined) window.clearTimeout(timer)
  }
}

function takeOverPendingPrime(slot: PlayerSoundSlot): void {
  if (!slot.primePromise) return
  slot.primeToken += 1
  slot.primePromise = null
  try {
    slot.audio.pause()
    resetAudio(slot.audio)
  } catch {
    // The real playback below remains the authoritative capability check.
  }
}

async function promiseResultBefore(
  promise: Promise<void>,
  deadline: number,
): Promise<PlayerSoundPlayResult> {
  const remaining = deadline - Date.now()
  if (remaining <= 0) return { outcome: 'stale' }
  let timer: number | undefined
  let abortPlayback: (() => void) | undefined
  try {
    return await Promise.race([
      Promise.resolve(promise).then(
        () => ({ outcome: 'scheduled' as const }),
        error => ({ outcome: 'failed' as const, errorName: errorName(error) }),
      ),
      new Promise<PlayerSoundPlayResult>(resolve => {
        timer = window.setTimeout(() => resolve({ outcome: 'stale' }), remaining)
      }),
      new Promise<PlayerSoundPlayResult>(resolve => {
        abortPlayback = () => resolve({
          outcome: 'failed',
          errorName: 'page_frozen',
        })
        playbackAbortListeners.add(abortPlayback)
      }),
    ])
  } finally {
    if (timer !== undefined) window.clearTimeout(timer)
    if (abortPlayback) playbackAbortListeners.delete(abortPlayback)
  }
}

function resetAudio(audio: HTMLAudioElement): void {
  try {
    audio.currentTime = 0
  } catch {
    // Some browsers can reject currentTime changes before metadata is loaded.
  }
}

async function claimAlert(eventKey: string): Promise<string | null> {
  if (hasSeenAlert(eventKey)) return null

  const nonce = randomToken()
  const key = claimStorageKey(eventKey)
  const claim: AlertRecord = {
    eventKey,
    tabID,
    nonce,
    expiresAt: Date.now() + claimTTLMS,
  }
  if (!writeRecord(key, claim)) {
    return hasSeenAlert(eventKey) ? null : nonce
  }

  await sleep(40 + Math.floor(Math.random() * 80))
  const current = readRecord(key)
  return (
    current &&
    current.eventKey === eventKey &&
    current.tabID === tabID &&
    current.nonce === nonce &&
    current.expiresAt > Date.now() &&
    !hasSeenAlert(eventKey)
  )
    ? nonce
    : null
}

function releaseAlertClaim(eventKey: string, nonce: string): void {
  const key = claimStorageKey(eventKey)
  const current = readRecord(key)
  if (
    current?.eventKey === eventKey &&
    current.tabID === tabID &&
    current.nonce === nonce
  ) {
    removeRecord(key)
  }
}

function firstObservedAt(
  eventKey: string,
  suggestedObservedAt = Date.now(),
): number {
  const now = Date.now()
  const candidateObservedAt = validObservedAt(suggestedObservedAt, now)
  const cached = observedAlerts.get(eventKey)
  if (cached) {
    if (cached.expiresAt > now) {
      return retainEarlierObservedAt(
        eventKey,
        cached,
        candidateObservedAt,
        now,
      )
    }
    observedAlerts.delete(eventKey)
  }

  const key = observedStorageKey(eventKey)
  const stored = readObservedRecord(key)
  if (
    stored?.eventKey === eventKey &&
    stored.expiresAt > now
  ) {
    observedAlerts.set(eventKey, stored)
    return retainEarlierObservedAt(
      eventKey,
      stored,
      candidateObservedAt,
      now,
    )
  }
  if (stored) removeRecord(key)

  const record: ObservedAlertRecord = {
    eventKey,
    observedAt: candidateObservedAt,
    expiresAt: now + observedTTLMS,
  }
  observedAlerts.set(eventKey, record)
  writeObservedRecord(key, record)
  return candidateObservedAt
}

function safeObservedAt(observedAt: number, now: number): number {
  return observedAt <= now ? observedAt : 0
}

function validObservedAt(observedAt: number, now: number): number {
  return (
    Number.isFinite(observedAt) &&
    observedAt >= 0 &&
    observedAt <= now
  )
    ? observedAt
    : now
}

function retainEarlierObservedAt(
  eventKey: string,
  record: ObservedAlertRecord,
  candidateObservedAt: number,
  now: number,
): number {
  const observedAt = Math.min(
    safeObservedAt(record.observedAt, now),
    candidateObservedAt,
  )
  if (observedAt !== record.observedAt) {
    const updated = { ...record, observedAt }
    observedAlerts.set(eventKey, updated)
    writeObservedRecord(observedStorageKey(eventKey), updated)
  }
  return observedAt
}

function forgetObservedAlert(eventKey: string): void {
  observedAlerts.delete(eventKey)
  removeRecord(observedStorageKey(eventKey))
}

function hasSeenAlert(eventKey: string): boolean {
  const now = Date.now()
  const cachedExpiry = seenAlerts.get(eventKey)
  if (cachedExpiry !== undefined) {
    if (cachedExpiry > now) return true
    seenAlerts.delete(eventKey)
  }

  const key = seenStorageKey(eventKey)
  const record = readRecord(key)
  if (!record) return false
  if (record.expiresAt <= now) {
    removeRecord(key)
    return false
  }
  if (record.eventKey !== eventKey) return false
  seenAlerts.set(eventKey, record.expiresAt)
  return true
}

function markSeenAlert(eventKey: string): void {
  sweepExpiredAlertRecords()
  const expiresAt = Date.now() + seenTTLMS
  seenAlerts.set(eventKey, expiresAt)
  writeRecord(seenStorageKey(eventKey), {
    eventKey,
    expiresAt,
  })
  forgetObservedAlert(eventKey)
}

function sweepExpiredAlertRecords(): void {
  const now = Date.now()
  if (now - lastStorageSweepAt < storageSweepIntervalMS) return
  lastStorageSweepAt = now
  for (const [eventKey, expiresAt] of seenAlerts) {
    if (expiresAt <= now) seenAlerts.delete(eventKey)
  }
  for (const [eventKey, record] of observedAlerts) {
    if (record.expiresAt <= now) observedAlerts.delete(eventKey)
  }
  try {
    const storage = window.localStorage
    if (
      typeof storage.length !== 'number' ||
      typeof storage.key !== 'function'
    ) {
      return
    }
    const expiredKeys: string[] = []
    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index)
      if (
        !key ||
        (
          !key.startsWith(seenStoragePrefix) &&
          !key.startsWith(claimStoragePrefix) &&
          !key.startsWith(observedStoragePrefix)
        )
      ) {
        continue
      }
      const record = readRecord(key)
      if (!record || record.expiresAt <= now) expiredKeys.push(key)
    }
    for (const key of expiredKeys) storage.removeItem(key)
  } catch {
    // Storage can disappear or become unavailable between individual calls.
  }
}

function browserLocks(): BrowserLockManager | null {
  return (navigator as Navigator & { locks?: BrowserLockManager }).locks ?? null
}

function alertLockName(eventKey: string): string {
  let hash = 2166136261
  for (let index = 0; index < eventKey.length; index += 1) {
    hash ^= eventKey.charCodeAt(index)
    hash = Math.imul(hash, 16777619)
  }
  return `${lockNamePrefix}:${(hash >>> 0).toString(36)}`
}

function isFocusedPage(): boolean {
  return typeof document !== 'undefined' &&
    document.visibilityState === 'visible' &&
    document.hasFocus()
}

function seenStorageKey(eventKey: string): string {
  return `${seenStoragePrefix}${eventKey}`
}

function claimStorageKey(eventKey: string): string {
  return `${claimStoragePrefix}${eventKey}`
}

function observedStorageKey(eventKey: string): string {
  return `${observedStoragePrefix}${eventKey}`
}

function readRecord(key: string): AlertRecord | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<AlertRecord>
    if (typeof parsed.eventKey !== 'string' || typeof parsed.expiresAt !== 'number') return null
    return {
      eventKey: parsed.eventKey,
      tabID: typeof parsed.tabID === 'string' ? parsed.tabID : undefined,
      nonce: typeof parsed.nonce === 'string' ? parsed.nonce : undefined,
      expiresAt: parsed.expiresAt,
    }
  } catch {
    return null
  }
}

function readObservedRecord(key: string): ObservedAlertRecord | null {
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) return null
    const parsed = JSON.parse(raw) as Partial<ObservedAlertRecord>
    if (
      typeof parsed.eventKey !== 'string' ||
      typeof parsed.observedAt !== 'number' ||
      !Number.isFinite(parsed.observedAt) ||
      typeof parsed.expiresAt !== 'number' ||
      !Number.isFinite(parsed.expiresAt)
    ) {
      return null
    }
    return {
      eventKey: parsed.eventKey,
      observedAt: parsed.observedAt,
      expiresAt: parsed.expiresAt,
    }
  } catch {
    return null
  }
}

function writeRecord(key: string, record: AlertRecord): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(record))
    return true
  } catch {
    return false
  }
}

function writeObservedRecord(
  key: string,
  record: ObservedAlertRecord,
): boolean {
  try {
    window.localStorage.setItem(key, JSON.stringify(record))
    return true
  } catch {
    return false
  }
}

function removeRecord(key: string): void {
  try {
    window.localStorage.removeItem(key)
  } catch {
    // localStorage can be disabled by browser policy.
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise(resolve => window.setTimeout(resolve, ms))
}

function randomToken(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

function readStoredVolume(): number {
  try {
    const raw = window.localStorage.getItem(volumeStorageKey)
    if (raw === null) return 0.15
    return clampVolume(Number(raw))
  } catch {
    return 0.15
  }
}

function defaultPlayerSoundVolumes(): Record<PlayerSoundID, number> {
  return Object.fromEntries(
    playerSoundIDs.map(soundID => [soundID, 1]),
  ) as Record<PlayerSoundID, number>
}

function readStoredSoundVolumes(): Record<PlayerSoundID, number> {
  const defaults = defaultPlayerSoundVolumes()
  try {
    const raw = window.localStorage.getItem(soundVolumesStorageKey)
    if (!raw) return defaults
    const parsed = JSON.parse(raw) as {
      version?: unknown
      volumes?: Partial<Record<PlayerSoundID, unknown>>
    }
    if (
      parsed.version !== 1 ||
      !parsed.volumes ||
      typeof parsed.volumes !== 'object'
    ) {
      return defaults
    }
    const volumes = { ...defaults }
    for (const soundID of playerSoundIDs) {
      const value = parsed.volumes[soundID]
      if (typeof value === 'number' && Number.isFinite(value)) {
        volumes[soundID] = clampVolume(value)
      }
    }
    return volumes
  } catch {
    return defaults
  }
}

function writeStoredSoundVolumes(): void {
  try {
    window.localStorage.setItem(soundVolumesStorageKey, JSON.stringify({
      version: 1,
      volumes: playerSoundVolumes,
    }))
  } catch {
    // Settings remain valid for this page when storage is unavailable.
  }
}

function notifySoundSettingsChanged(): void {
  const snapshot = getPlayerSoundSettings()
  for (const listener of soundSettingsListeners) {
    try {
      listener(snapshot)
    } catch {
      // One settings surface must not block the audio runtime.
    }
  }
}

function isPlayerSoundID(value: string): value is PlayerSoundID {
  return (playerSoundIDs as readonly string[]).includes(value)
}

function handleSoundStorage(event: StorageEvent): void {
  if (event.key === volumeStorageKey) {
    soundVolume = readStoredVolume()
    notifySoundSettingsChanged()
    return
  }
  if (event.key === soundVolumesStorageKey) {
    playerSoundVolumes = readStoredSoundVolumes()
    notifySoundSettingsChanged()
  }
}

function handleSoundFreeze(): void {
  pageFrozen = true
  for (const abort of [...playbackAbortListeners]) abort()
  for (const timer of criticalRetryTimers.values()) window.clearTimeout(timer)
  criticalRetryTimers.clear()
}

function handleSoundResume(): void {
  pageFrozen = false
  // App.vue performs an authoritative matchmaker refresh on resume/pageshow.
  // That fresh state, rather than a possibly ended pre-freeze attempt, decides
  // whether a critical sound is still eligible for retry.
}

function clampVolume(volume: number): number {
  if (!Number.isFinite(volume)) return 1
  return Math.max(0, Math.min(1, volume))
}

if (
  typeof window !== 'undefined' &&
  typeof window.addEventListener === 'function'
) {
  window.addEventListener('storage', handleSoundStorage)
}

if (
  typeof document !== 'undefined' &&
  typeof document.addEventListener === 'function'
) {
  document.addEventListener('freeze', handleSoundFreeze)
  document.addEventListener('resume', handleSoundResume)
}
